/**
 * Pure trace builders for the portfolio-exposures chart.
 *
 * Used by both the MARS-Live PortfolioExposuresChart and the portal
 * chat's inline exposures chart. Each caller layers its own `chartLayout`
 * preset on top — this module only owns the Plotly `data[]` shape.
 */
import type { Data } from 'plotly.js';
import { hexToRgba } from './colors';
import { hoverTemplate } from './hoverTemplates';

/** Shape of the exposures sub-payload from /api/mars/portfolio/<fund_id>/timeseries. */
export interface ExposuresPayload {
  dates: string[];
  /** Series keyed by display name ("Equities", "Bonds", "Commodities", "Inflation"). */
  series: Record<string, Array<number | null>>;
  /** Steady-state strategic weights per asset, already in percent (e.g. 22 for 22%). */
  steady_state_strategic?: Record<string, number>;
}

/** Date-windowed subset with all values multiplied by 100 for display. */
export interface SlicedExposures {
  dates: string[];
  series: Record<string, number[]>;
}

/**
 * Filter dates to `[startDate, endDate]`, keep only `assetKeys`, and
 * multiply by 100 (raw payload is in decimal weights).
 *
 * Returns an empty result when the window has no rows.
 */
export function sliceExposures(
  payload: ExposuresPayload,
  assetKeys: string[],
  startDate?: string | null,
  endDate?: string | null,
): SlicedExposures {
  const series: Record<string, number[]> = {};
  for (const k of assetKeys) series[k] = [];
  const dates: string[] = [];
  for (let i = 0; i < payload.dates.length; i++) {
    const d = payload.dates[i];
    if (startDate && d < startDate) continue;
    if (endDate && d > endDate) continue;
    dates.push(d);
    for (const k of assetKeys) {
      const v = payload.series[k]?.[i];
      series[k].push(v == null ? 0 : v * 100);
    }
  }
  return { dates, series };
}

export interface ExposureAsset {
  /** Series key in the sliced payload (e.g. "Equities"). */
  key: string;
  /** Legend / hover label (e.g. "Fixed Income"). */
  label: string;
  /** Brand colour for the main line. */
  color: string;
}

/**
 * Main per-asset lines, plus an optional half-opacity overlay (used by
 * the live page's "Actual Traded" toggle) and optional dotted strategic
 * lines. Strategic weights are assumed already in percent.
 */
export function buildExposuresTraces(
  primary: SlicedExposures,
  opts: {
    assets: ReadonlyArray<ExposureAsset>;
    overlay?: SlicedExposures | null;
    strategic?: Record<string, number> | null;
    /** Main-line width; defaults to the historical 3. */
    lineWidth?: number;
    /**
     * Show the dotted strategic lines in the legend (default true). The
     * compact live page turns this off — the entries double the legend and
     * the dotted line already reads as "strategic" next to its asset line.
     */
    strategicInLegend?: boolean;
  },
): Data[] {
  const out: Data[] = [];

  for (const a of opts.assets) {
    const y = primary.series[a.key];
    if (!y || !y.length) continue;
    out.push({
      type: 'scatter',
      mode: 'lines',
      name: a.label,
      x: primary.dates,
      y,
      line: { color: a.color, width: opts.lineWidth ?? 3 },
      hovertemplate: hoverTemplate({ name: a.label, format: '.1f', suffix: '%' }),
    });
  }

  if (opts.overlay) {
    for (const a of opts.assets) {
      const y = opts.overlay.series[a.key];
      if (!y || !y.length) continue;
      out.push({
        type: 'scatter',
        mode: 'lines',
        name: `${a.label} (Actual Traded)`,
        x: opts.overlay.dates,
        y,
        line: { color: hexToRgba(a.color, 0.5), width: 2 },
        showlegend: false,
        hovertemplate: hoverTemplate({
          name: `${a.label} (Actual Traded)`,
          format: '.1f',
          suffix: '%',
        }),
      });
    }
  }

  if (opts.strategic && primary.dates.length > 1) {
    const x0 = primary.dates[0];
    const x1 = primary.dates[primary.dates.length - 1];
    for (const a of opts.assets) {
      const level = opts.strategic[a.key];
      if (level == null) continue;
      out.push({
        type: 'scatter',
        mode: 'lines',
        name: `${a.label} Strategic`,
        x: [x0, x1],
        y: [level, level],
        line: { color: hexToRgba(a.color, 0.5), width: 2, dash: 'dot' },
        showlegend: opts.strategicInLegend ?? true,
        hoverinfo: 'skip',
      });
    }
  }

  return out;
}

/**
 * Y-range from the visible data with 10% padding (matches the live chart).
 * Pass the overlay slice and strategic levels too when they are drawn, so
 * every visible line fits inside the range.
 */
export function computeExposuresYRange(
  s: SlicedExposures,
  overlay?: SlicedExposures | null,
  levels?: readonly number[],
): [number, number] {
  const vals: number[] = [];
  const collect = (slice: SlicedExposures): void => {
    for (const arr of Object.values(slice.series)) {
      for (const v of arr) if (Number.isFinite(v)) vals.push(v);
    }
  };
  collect(s);
  if (overlay) collect(overlay);
  for (const v of levels ?? []) if (Number.isFinite(v)) vals.push(v);
  if (!vals.length) return [0, 5];
  let yMin = vals[0];
  let yMax = vals[0];
  for (const v of vals) {
    if (v < yMin) yMin = v;
    if (v > yMax) yMax = v;
  }
  const pad = (yMax - yMin) * 0.1 || 0.5;
  return [yMin - pad, yMax + pad];
}
